import React from 'react';
import {
    Checkbox,
    Spinner,
    Text,
    Divider,
    Badge,
    Input,
    Button,
    Dropdown,
    Option,
    Menu,
    MenuTrigger,
    MenuButton,
    MenuPopover,
    MenuList,
    MenuItemCheckbox,
    FluentProvider,
    webDarkTheme,
    webLightTheme,
} from '@fluentui/react-components';
import { FilterRegular, DismissRegular, TagRegular } from '@fluentui/react-icons';
import { useGetFacetOptionsQuery } from '../../Api/Api';
import { IPriceRange } from '../../Api/Models/Products';
import { FacetSidebarLayout, importantStyleCss, FEATURED_TAG } from '../Helpers/searchPageConfig';
import { tagColor } from '../Helpers/ColorHelpers';
import { useLocalization } from '../Helpers/useLocalization';
import { useFavorites } from '../Helpers/useFavorites';

export interface IActiveFacet {
    name: string;
    value: string;
}

interface FacetFiltersProps {
    layout: FacetSidebarLayout;
    segment?: string;
    category?: string;
    subCategory?: string;
    keywords?: string;
    active: IActiveFacet[];
    onToggle: (name: string, value: string) => void;
    // Navigation — the panel is the primary way to pick a starting segment/category/sub-category when enabled.
    segmentOptions: string[];
    categoryOptions: string[];
    onSelectSegment: (segment?: string) => void;
    onSelectCategory: (category?: string) => void;
    onSelectSubCategory: (subCategory?: string) => void;
    manufacturer?: string;
    onSelectManufacturer: (manufacturer?: string) => void;
    // Reset every filter/scope the panel controls back to the base search.
    onClearAll: () => void;
    // Dark storefront background → light-on-dark styling for the panel's own text/chips.
    isDark?: boolean;
    // Price filtering (only rendered when the storefront enables it).
    priceFilter?: boolean;
    minPrice?: string;
    maxPrice?: string;
    onApplyPrice?: (min?: string, max?: string) => void;
    // Merchant-configured container styling (searchPageConfig.facetPanel), applied to the
    // panel/inline wrapper. Passed straight through as an inline style.
    panelStyle?: React.CSSProperties;
    // Merchant-configured styling for the facet section headings (searchPageConfig.facetHeading),
    // injected as a scoped rule so it beats Fluent's forced Text color.
    headingStyle?: React.CSSProperties;
    // Product tag filtering (only when the storefront enables tags). Tag values + counts come
    // from the facet-options response (data.tags); onToggleTag toggles a ?tag= filter.
    tagsEnabled?: boolean;
    // Prepend the synthetic "Featured" tag (per-storefront featured flag) to the tag options, so it
    // can be toggled from the panel like a normal tag. It carries no aggregated count.
    includeFeaturedTag?: boolean;
    activeTags?: string[];
    onToggleTag?: (value: string) => void;
    // Favorites-only filter (only when the storefront enables favorites AND the shopper is signed in —
    // FavoriteFilter itself decides that, so the page can pass these unconditionally).
    favoritesOnly?: boolean;
    onToggleFavoritesOnly?: (on: boolean) => void;
}

const ALL = '__all__';

// A single-select navigation dropdown with an "all" option to clear it.
const NavField: React.FC<{
    label: string;
    value?: string;
    options: string[];
    placeholder: string;
    disabled?: boolean;
    onChange: (value?: string) => void;
}> = ({ label, value, options, placeholder, disabled, onChange }) => (
    <div className="sf-nav-field">
        <Text size={200} weight="semibold" className="sf-facet-group-title">{label}</Text>
        <Dropdown
            size="small"
            disabled={disabled}
            placeholder={placeholder}
            value={value ?? ''}
            selectedOptions={value ? [value] : []}
            onOptionSelect={(_e, d) => onChange(d.optionValue === ALL ? undefined : d.optionValue)}
        >
            <Option value={ALL} text={placeholder}>{placeholder}</Option>
            {options.map((o) => (
                <Option key={o} value={o} text={o}>{o}</Option>
            ))}
        </Dropdown>
    </div>
);

// A min/max price control seeded from the current URL price params and the aggregated bounds.
const PriceFilter: React.FC<{
    layout: FacetSidebarLayout;
    range?: IPriceRange;
    min?: string;
    max?: string;
    onApply: (min?: string, max?: string) => void;
}> = ({ layout, range, min, max, onApply }) => {
    const L = useLocalization();
    const [minInput, setMinInput] = React.useState(min ?? '');
    const [maxInput, setMaxInput] = React.useState(max ?? '');
    React.useEffect(() => { setMinInput(min ?? ''); }, [min]);
    React.useEffect(() => { setMaxInput(max ?? ''); }, [max]);

    // Apply on blur / Enter (no button — the range fires like the other filters). Skip a redundant
    // apply when nothing changed so we don't kick off an unnecessary search on every blur.
    const apply = () => {
        const nextMin = minInput || undefined;
        const nextMax = maxInput || undefined;
        if (nextMin === (min || undefined) && nextMax === (max || undefined)) return;
        onApply(nextMin, nextMax);
    };
    const onKey = (e: React.KeyboardEvent) => { if (e.key === 'Enter') apply(); };
    const minPlaceholder = range?.min != null ? Math.floor(range.min).toString() : 'Min';
    const maxPlaceholder = range?.max != null ? Math.ceil(range.max).toString() : 'Max';

    return (
        <div className={`sf-price-filter sf-price-${layout}`}>
            <Text weight="semibold" size={200} className="sf-facet-group-title">{L('localization_facets_price_heading')}</Text>
            <div className="sf-price-inputs">
                <Input size="small" type="number" value={minInput} placeholder={minPlaceholder}
                    onChange={(_e, d) => setMinInput(d.value)} onKeyDown={onKey} onBlur={apply} />
                <span>&ndash;</span>
                <Input size="small" type="number" value={maxInput} placeholder={maxPlaceholder}
                    onChange={(_e, d) => setMaxInput(d.value)} onKeyDown={onKey} onBlur={apply} />
            </div>
        </div>
    );
};

// Shopper-facing filter panel. When enabled it replaces the breadcrumb chips: it drives navigation via
// single-select segment/category/sub-category dropdowns, shows the active manufacturer, and — once a segment
// AND category are chosen — surfaces the storefront's searchable attributes (values+counts) and price range.
const FacetFilters: React.FC<FacetFiltersProps> = (props) => {
    const L = useLocalization();
    const { layout, segment, category, subCategory, keywords, active, onToggle } = props;
    const { segmentOptions, categoryOptions, onSelectSegment, onSelectCategory, onSelectSubCategory } = props;
    const { manufacturer, onSelectManufacturer, onClearAll, isDark, priceFilter, minPrice, maxPrice, onApplyPrice } = props;
    // Category alone is a valid scope for aggregations (segment optional) — the home page's category pods
    // navigate with just a category, so we still resolve/show attribute options from that.
    const ready = !!category;
    // Off for guests and for storefronts with favorites disabled, so the filter is never offered where
    // it could only ever return nothing.
    const { enabled: favoritesEnabled } = useFavorites();
    const hasCriteria = !!segment || !!category || !!subCategory || !!manufacturer
        || active.length > 0 || (props.activeTags?.length ?? 0) > 0 || !!minPrice || !!maxPrice
        || !!props.favoritesOnly;

    // Sub-category is passed so the manufacturer list narrows with it; the API applies it only to the
    // manufacturers aggregation, so the sub-category list itself stays complete for the whole category.
    const { data, isFetching } = useGetFacetOptionsQuery(
        { segment, category, subCategory, keywords },
        { skip: !ready }
    );

    const isActive = (name: string, value: string) => active.some((f) => f.name === name && f.value === value);
    const selectedValues = (name: string) => active.filter((f) => f.name === name).map((f) => f.value);

    const facets = ready ? (data?.facets ?? []) : [];
    const subCategoryOptions = ready ? (data?.subCategories ?? []) : [];
    const manufacturerOptions = ready ? (data?.manufacturers ?? []) : [];
    const showPrice = !!priceFilter && !!onApplyPrice;
    const baseTagOptions = ready ? (data?.tags ?? []) : [];
    // The synthetic "Featured" option leads the list when requested; it has no aggregated count.
    const tagOptions: { value: string; count?: number }[] = props.includeFeaturedTag
        ? [{ value: FEATURED_TAG }, ...baseTagOptions.filter((t) => t.value !== FEATURED_TAG)]
        : baseTagOptions;
    const activeTags = props.activeTags ?? [];
    const showTags = !!props.tagsEnabled && !!props.onToggleTag && tagOptions.length > 0;

    // Applied-tag pills: shown whenever tag filters are active, regardless of whether a category
    // scope has loaded the tag facet list. This is the only place a tag applied from a product card
    // (with no other filters, so no facet options are fetched) can be seen and removed.
    const showAppliedTags = !!props.onToggleTag && activeTags.length > 0;
    const appliedTags = showAppliedTags ? (
        <div className="sf-applied-tags">
            <Text weight="semibold" size={200} className="sf-facet-group-title">{L('localization_facets_active_tags')}</Text>
            <div className="sf-applied-tag-chips">
                {activeTags.map((t) => {
                    const c = tagColor(t);
                    return (
                        <button
                            key={t}
                            type="button"
                            className="sf-applied-tag-chip"
                            style={{ backgroundColor: c.background, color: c.color }}
                            title={`Remove ${t} filter`}
                            onClick={() => props.onToggleTag!(t)}
                        >
                            <TagRegular className="sf-applied-tag-icon" />
                            <span>{t}</span>
                            <DismissRegular className="sf-applied-tag-dismiss" />
                        </button>
                    );
                })}
            </div>
        </div>
    ) : null;

    // Favorites-only. Hidden for guests and when the storefront has favorites off (useFavorites().enabled),
    // so it never offers a filter that would return nothing.
    const favorites = props.onToggleFavoritesOnly && favoritesEnabled ? (
        <Checkbox
            className="sf-facet-favorites"
            label={L('localization_favorites_filter')}
            checked={!!props.favoritesOnly}
            onChange={(_, d) => props.onToggleFavoritesOnly!(d.checked === true)}
        />
    ) : null;

    // Navigation section — always shown, so the panel can pick the starting segment/category/sub-category.
    const nav = (
        <div className="sf-facet-nav">
            <NavField label={L('localization_facets_segment_label')} value={segment} options={segmentOptions}
                placeholder={L('localization_facets_segment_all')} onChange={onSelectSegment} />
            <NavField label={L('localization_facets_category_label')} value={category} options={categoryOptions}
                placeholder={L('localization_facets_category_all')} disabled={categoryOptions.length === 0 && !category}
                onChange={onSelectCategory} />
            <NavField label={L('localization_facets_subcategory_label')} value={subCategory} options={subCategoryOptions}
                placeholder={L('localization_facets_subcategory_all')} disabled={!ready || subCategoryOptions.length === 0}
                onChange={onSelectSubCategory} />
            {/* Enabled once a category scopes the manufacturer aggregation, or whenever one is already active
                (so it can always be changed/cleared). */}
            <NavField label={L('localization_facets_manufacturer_label')} value={manufacturer} options={manufacturerOptions}
                placeholder={L('localization_facets_manufacturer_all')} disabled={manufacturerOptions.length === 0 && !manufacturer}
                onChange={onSelectManufacturer} />
        </div>
    );

    const price = showPrice ? (
        <PriceFilter layout={layout} range={data?.priceRange} min={minPrice} max={maxPrice} onApply={onApplyPrice!} />
    ) : null;

    const clearButton = hasCriteria ? (
        <Button size="small" appearance="subtle" icon={<DismissRegular />} onClick={onClearAll} className="sf-facet-clear">
            {L('localization_facets_clear_filters')}
        </Button>
    ) : null;

    // Attribute filters (+ price) only once a segment AND category scope them.
    let body: React.ReactNode = null;
    if (ready) {
        if (isFetching) {
            body = <Spinner size="tiny" label={layout === 'panel' ? 'Loading filters…' : undefined} />;
        } else if (layout === 'inline') {
            body = (
                <>
                    {facets.map((facet) => {
                        const selected = selectedValues(facet.key);
                        return (
                            <Menu
                                key={facet.key}
                                checkedValues={{ [facet.key]: selected }}
                                onCheckedValueChange={(_e, d) => {
                                    const after = d.checkedItems;
                                    const added = after.find((v) => !selected.includes(v));
                                    const removed = selected.find((v) => !after.includes(v));
                                    const changed = added ?? removed;
                                    if (changed) onToggle(d.name, changed);
                                }}
                            >
                                <MenuTrigger disableButtonEnhancement>
                                    <MenuButton size="small" icon={<FilterRegular />}>
                                        {facet.key}{selected.length > 0 ? ` (${selected.length})` : ''}
                                    </MenuButton>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        {facet.values.map((v) => (
                                            <MenuItemCheckbox key={v.value} name={facet.key} value={v.value}>
                                                {v.value} <Badge appearance="ghost" size="small">{v.count}</Badge>
                                            </MenuItemCheckbox>
                                        ))}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        );
                    })}
                    {showTags && (
                        <Menu
                            checkedValues={{ tags: activeTags }}
                            onCheckedValueChange={(_e, d) => {
                                const after = d.checkedItems;
                                const added = after.find((v) => !activeTags.includes(v));
                                const removed = activeTags.find((v) => !after.includes(v));
                                const changed = added ?? removed;
                                if (changed) props.onToggleTag!(changed);
                            }}
                        >
                            <MenuTrigger disableButtonEnhancement>
                                <MenuButton size="small" icon={<TagRegular />}>
                                    {L('localization_facets_tags_button')}{activeTags.length > 0 ? ` (${activeTags.length})` : ''}
                                </MenuButton>
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    {tagOptions.map((t) => (
                                        <MenuItemCheckbox key={t.value} name="tags" value={t.value}>
                                            {t.value} {t.count != null && <Badge appearance="ghost" size="small">{t.count}</Badge>}
                                        </MenuItemCheckbox>
                                    ))}
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    )}
                    {price}
                </>
            );
        } else {
            body = (
                <>
                    {facets.map((facet, i) => (
                        <div className="sf-facet-group" key={facet.key}>
                            {i > 0 && <Divider style={{ margin: '0.5em 0' }} />}
                            <Text weight="semibold" size={200} className="sf-facet-group-title">{facet.key}</Text>
                            <div className="sf-facet-values">
                                {facet.values.map((v) => (
                                    <Checkbox
                                        key={v.value}
                                        label={`${v.value} (${v.count})`}
                                        checked={isActive(facet.key, v.value)}
                                        onChange={() => onToggle(facet.key, v.value)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                    {showTags && (
                        <div className="sf-facet-group">
                            {facets.length > 0 && <Divider style={{ margin: '0.5em 0' }} />}
                            <Text weight="semibold" size={200} className="sf-facet-group-title">{L('localization_facets_tags_button')}</Text>
                            <div className="sf-facet-values">
                                {tagOptions.map((t) => {
                                    const c = tagColor(t.value);
                                    return (
                                        <div key={t.value} className="sf-tag-filter-row">
                                            <span className="sf-tag-swatch" style={{ backgroundColor: c.background }} />
                                            <Checkbox
                                                label={t.count != null ? `${t.value} (${t.count})` : t.value}
                                                checked={activeTags.includes(t.value)}
                                                onChange={() => props.onToggleTag!(t.value)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {price && (
                        <div className="sf-facet-group">
                            {(facets.length > 0 || showTags) && <Divider style={{ margin: '0.5em 0' }} />}
                            {price}
                        </div>
                    )}
                </>
            );
        }
    }

    // Theme the Fluent controls (dropdowns, checkboxes, menus) to the storefront's luminance. The provider
    // is display:contents (see CSS) so it only supplies theme context — it doesn't alter the panel layout.
    const darkClass = isDark ? ' sf-facets-dark' : '';
    const theme = isDark ? webDarkTheme : webLightTheme;
    if (layout === 'inline') {
        return (
            <div className={`sf-facets sf-facets-inline${darkClass}`} style={props.panelStyle}>
                <FluentProvider theme={theme} className="sf-facets-theme">{favorites}{nav}{appliedTags}{body}{clearButton}</FluentProvider>
            </div>
        );
    }
    return (
        <div className={`sf-facets sf-facets-panel${darkClass}`} style={props.panelStyle}>
            {props.headingStyle && Object.keys(props.headingStyle).length > 0 && (
                <style>{`.sf-facets .sf-facet-group-title { ${importantStyleCss(props.headingStyle)} }`}</style>
            )}
            <FluentProvider theme={theme} className="sf-facets-theme">
                {clearButton && <div className="sf-facet-header">{clearButton}</div>}
                {favorites}
                {nav}
                {appliedTags && <Divider style={{ margin: '0.75em 0' }} />}
                {appliedTags}
                {ready && <Divider style={{ margin: '0.75em 0' }} />}
                {body}
            </FluentProvider>
        </div>
    );
};

export default FacetFilters;
